import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { twMerge } from 'tailwind-merge';
import { useForceRender } from '../../../hooks/useForceRender';

export interface MarkdownTextareaRef {
    setInitialContent: (content: string) => void;
    getContent: () => string;
    isDirty: () => boolean;
}

export type MarkdownTextareaChangeEvent = {
    event: React.FormEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLTextAreaElement>;
    content: string;
    isDirty: boolean;
    isBlur: boolean;
};

/**
 * 마크다운 텍스트 에디터에서 Textarea 영역
 * - 자동으로 늘어나고 상태를 가져올 수 있다
 */
export const MarkdownTextarea = observer(
    ({
        ref,
        minRows,
        maxRows,
        onChange,
        className,
        disabled,
        placeholder,
    }: {
        /**
         * 에디터는 설정값을 props 로 넘기면 관리가 애매하므로 명시적으로 ref 로 처리한다
         */
        ref?: React.Ref<MarkdownTextareaRef>;

        minRows?: number;
        maxRows?: number;
        onChange?: (event: MarkdownTextareaChangeEvent) => void;
        className?: string;
        disabled?: boolean;
        placeholder?: string;
    }) => {
        const id = useMemo(() => {
            return _.uniqueId('markdown-textarea-');
        }, []);

        const forceRender = useForceRender();

        const initialContent = useRef('');
        const currentContent = useRef(''); // 실시간 변경 내용 추적을 위함

        const isDirty = useCallback(() => {
            return initialContent.current !== currentContent.current;
        }, []);

        /**
         * 실시간 변경 내용을 추적하기 위해 실제 값을 레퍼런스에 두고 본문이 변경될 때 강제로 렌더링을 호출한다
         */
        const updateContent = useCallback(
            (newContent: string, force = false) => {
                currentContent.current = newContent;

                // [순서 주의] 먼저 값을 업데이트 하고 나서 렌더링을 호출한다
                if (isDirty() || force) {
                    forceRender();
                    onChange?.({
                        event: {
                            target: {
                                value: newContent,
                            } as HTMLTextAreaElement,
                        } as unknown as React.FormEvent<HTMLTextAreaElement>,
                        content: newContent,
                        isDirty: isDirty(),
                        isBlur: false,
                    });
                }
            },
            [isDirty, forceRender],
        );

        useImperativeHandle(ref, () => {
            return {
                setInitialContent: (content = '') => {
                    initialContent.current = content;
                    updateContent(content, true);
                },
                getContent: () => {
                    return currentContent.current;
                },
                isDirty,
            };
        }, []);

        return (
            <TextareaAutosize
                id={id}
                className={twMerge(
                    'textarea textarea-bordered min-h-0 w-full resize-none',
                    className,
                )}
                autoCorrect="off"
                minRows={minRows}
                maxRows={maxRows}
                autoCapitalize="off"
                spellCheck={false}
                disabled={disabled}
                value={currentContent.current}
                placeholder={placeholder}
                onInput={(e) => {
                    const newContent = (e.target as HTMLTextAreaElement).value;

                    updateContent(newContent);

                    onChange?.({
                        event: e,
                        content: newContent,
                        isDirty: isDirty(),
                        isBlur: false,
                    });
                }}
                onBlur={(e) => {
                    onChange?.({
                        event: e,
                        content: currentContent.current,
                        isDirty: isDirty(),
                        isBlur: true,
                    });
                }}
            />
        );
    },
);
